import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import {
  fetchApiRequest,
  getApiHost,
  isMasterOrg,
  canAccessOrgAdmin,
  renderBoilerPlate,
} from './Common';
import { UserContext } from './UserContext';

type WorkHours = { start_time?: string; end_time?: string };
type WorkDays = { start_day?: number; end_day?: number };
type Schedule = {
  org_slug: string;
  timezone: string;
  work_hours?: WorkHours;
  work_days?: WorkDays;
  speech_lifetime: boolean;
  save_human_speech: boolean;
};
type BreakRow = {
  id: number;
  org_slug: string;
  break_type: string;
  starts_at: string;
  ends_at: string;
  label?: string;
};
type UserRow = { email: string; org_slug: string };
type ProvisioningStep = { id: string; title: string; detail: string; link?: string };
type CreateOrgResult = {
  org_slug: string;
  device_id_prefix: string;
  bq_requests_table: string;
  provisioning: ProvisioningStep[];
  bq_view_created?: boolean;
  bq_message?: string;
  looker_linking_url?: string;
  looker_report_id?: string;
  looker_embed_url?: string;
  customer_runbook_path?: string;
};

const DAY_LABELS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday',
];

const CASUAL_EVENT = 'Casual';

const pageLayout: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const formFieldSx = {
  '& .MuiOutlinedInput-input, & .MuiSelect-select': {
    padding: '0.4rem 0.4rem',
    width: 'auto',
  },
  '& .MuiPickersOutlinedInput-root': {
    padding: '0 0.4rem',
  },
  '& .MuiPickersSectionList-root': {
    padding: '0.4rem 0',
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(14px, 7px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
};

const timePickerSlotProps = {
  textField: {
    sx: {
      '& .MuiPickersOutlinedInput-root': {
        padding: '0 0.4rem',
      },
      '& .MuiPickersSectionList-root': {
        padding: '0.4rem 0',
      },
    },
  },
};

function ensureCasualEvent(eventList: string[]): string[] {
  const rest = eventList.filter((e) => e !== CASUAL_EVENT);
  return [CASUAL_EVENT, ...rest];
}

function parseTime(value?: string): Dayjs | null {
  if (!value) return null;
  const parts = value.split(':');
  return dayjs().hour(Number(parts[0])).minute(Number(parts[1])).second(0);
}

function formatTime(value: Dayjs | null): string | undefined {
  if (!value) return undefined;
  return value.format('HH:mm:ss');
}

const OrgAdmin: React.FC<RouteComponentProps> = () => {
  const user = useContext(UserContext);
  const userOrg = user.profile?.org as string | undefined;
  const isMaster = isMasterOrg(user.profile);
  const [orgs, setOrgs] = useState<{ name: string }[]>([]);
  const [orgSlug, setOrgSlug] = useState(userOrg || '');
  const [tab, setTab] = useState(0);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [events, setEvents] = useState<string[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [newEvent, setNewEvent] = useState('');
  const [breaks, setBreaks] = useState<BreakRow[]>([]);
  const [timezones, setTimezones] = useState(['Europe/Stockholm']);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [breakType, setBreakType] = useState('duration');
  const [durationHours, setDurationHours] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [untilClock, setUntilClock] = useState<Dayjs | null>(null);
  const [rangeStart, setRangeStart] = useState<Dayjs | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Dayjs | null>(null);
  const [breakLabel, setBreakLabel] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [newOrgTimezone, setNewOrgTimezone] = useState('Europe/Stockholm');
  const [newOrgDevicePrefix, setNewOrgDevicePrefix] = useState('');
  const [newOrgBqTable, setNewOrgBqTable] = useState('');
  const [newOrgInitialEmail, setNewOrgInitialEmail] = useState('');
  const [provisioning, setProvisioning] = useState<ProvisioningStep[] | null>(null);
  const [createOrgResult, setCreateOrgResult] = useState<CreateOrgResult | null>(null);
  const [provisionBq, setProvisionBq] = useState(true);
  const [provisionLooker, setProvisionLooker] = useState(true);
  const [newOrgLookerReportId, setNewOrgLookerReportId] = useState('');
  const [registerLookerId, setRegisterLookerId] = useState('');

  const adminFetch = useCallback((path: string, init: RequestInit = {}) => {
    return new Promise<Response>((resolve, reject) => {
      getApiHost((apiHost) => {
        fetchApiRequest(`${apiHost}${path}`, (promise) => {
          promise.then(resolve).catch(reject);
        }, init);
      });
    });
  }, []);

  const loadConfig = useCallback(async (slug: string) => {
    setError(null);
    try {
      const res = await adminFetch(`/v1/admin/org_config?org_slug=${slug}`);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.detail || res.statusText);
      }
      const data = await res.json();
      setSchedule(data.schedule);
      setEvents(ensureCasualEvent(data.events || []));
      setUsers(data.users || []);
      setBreaks(data.breaks || []);
      setTimezones(data.timezones || ['Europe/Stockholm']);
    } catch (e: any) {
      setError(String(e.message || e));
    }
  }, [adminFetch]);

  useEffect(() => {
    if (!userOrg) return;
    if (isMaster) {
      getApiHost((apiHost) => {
        fetchApiRequest(`${apiHost}/v1/orgs`, (promise) => {
          promise.then((r) => r.json()).then((data) => {
            if (data.orgs) {
              const list = data.orgs.map((o: { name: string }) => ({ name: o.name }));
              setOrgs(list);
              if (!orgSlug && list.length > 0) {
                setOrgSlug(list[0].name);
              }
            }
          });
        });
      });
    } else {
      setOrgs([{ name: userOrg }]);
      setOrgSlug(userOrg);
    }
  }, [userOrg, isMaster]);

  useEffect(() => {
    if (orgSlug) loadConfig(orgSlug);
  }, [orgSlug, loadConfig]);

  if (!user.profile || !userOrg) {
    return renderBoilerPlate(
      <div style={pageLayout}>
        <div>
          <Paper>
            <Typography variant="subtitle1" sx={{ mb: '2rem' }}>Organization settings</Typography>
            <Alert severity="warning">Sign in to manage your organization.</Alert>
          </Paper>
        </div>
      </div>,
    );
  }

  if (!canAccessOrgAdmin(user.profile)) {
    return renderBoilerPlate(
      <div style={pageLayout}>
        <div>
          <Paper>
            <Typography variant="subtitle1" sx={{ mb: '2rem' }}>Organization settings</Typography>
            <Alert severity="warning">
              Org admin access is required to view Settings.
            </Alert>
          </Paper>
        </div>
      </div>,
    );
  }

  const saveSchedule = async () => {
    if (!schedule) return;
    setMessage(null);
    const res = await adminFetch('/v1/admin/org_config/schedule', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(schedule),
    });
    if (res.ok) {
      setMessage('Schedule saved.');
      loadConfig(orgSlug);
    } else {
      const body = await res.json();
      setError(body.detail || 'Failed to save schedule');
    }
  };

  const saveEvents = async () => {
    const res = await adminFetch('/v1/admin/org_config/events', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        org_slug: orgSlug,
        events: ensureCasualEvent(events),
      }),
    });
    if (res.ok) {
      setMessage('Events saved.');
      loadConfig(orgSlug);
    } else {
      const body = await res.json();
      setError(body.detail || 'Failed to save events');
    }
  };

  const addEvent = () => {
    const name = newEvent.trim();
    if (!name || events.includes(name)) return;
    setEvents([...events, name]);
    setNewEvent('');
  };

  const removeEvent = (index: number) => {
    if (events[index] === CASUAL_EVENT) return;
    setEvents(events.filter((_, i) => i !== index));
  };

  const addUser = async () => {
    const res = await adminFetch('/v1/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newUserEmail, org_slug: orgSlug }),
    });
    if (res.ok) {
      setNewUserEmail('');
      setMessage('User added.');
      loadConfig(orgSlug);
    } else {
      const body = await res.json();
      setError(body.detail || 'Failed to add user');
    }
  };

  const removeUser = async (email: string) => {
    const res = await adminFetch(
      `/v1/admin/users?email=${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    );
    if (res.ok) loadConfig(orgSlug);
  };

  const addBreak = async () => {
    const payload: Record<string, unknown> = {
      org_slug: orgSlug,
      break_type: breakType,
      label: breakLabel || undefined,
      timezone: schedule?.timezone,
    };
    if (breakType === 'duration') {
      payload.duration_hours = durationHours;
      payload.duration_minutes = durationMinutes;
    } else if (breakType === 'until_clock') {
      payload.until_clock = untilClock?.format('HH:mm');
    } else {
      payload.start_clock = rangeStart?.format('HH:mm');
      payload.end_clock = rangeEnd?.format('HH:mm');
    }
    const res = await adminFetch('/v1/admin/prediction_breaks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      setMessage('Prediction break added.');
      loadConfig(orgSlug);
    } else {
      const body = await res.json();
      setError(body.detail || 'Failed to add break');
    }
  };

  const reloadOrgsList = useCallback(() => {
    if (!isMaster) return;
    getApiHost((apiHost) => {
      fetchApiRequest(`${apiHost}/v1/orgs`, (promise) => {
        promise.then((r) => r.json()).then((data) => {
          if (data.orgs) {
            const list = data.orgs.map((o: { name: string }) => ({ name: o.name }));
            setOrgs(list);
          }
        });
      });
    });
  }, [isMaster]);

  const previewProvisioning = async () => {
    const slug = newOrgSlug.trim().toLowerCase();
    if (!slug) return;
    setError(null);
    setCreateOrgResult(null);
    const params = new URLSearchParams({ slug });
    if (newOrgDevicePrefix.trim()) {
      params.set('device_id_prefix', newOrgDevicePrefix.trim());
    }
    if (newOrgBqTable.trim()) {
      params.set('bq_requests_table', newOrgBqTable.trim());
    }
    try {
      const res = await adminFetch(
        `/v1/admin/organizations/provisioning_checklist?${params}`,
      );
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.detail || res.statusText);
      }
      const data: CreateOrgResult = await res.json();
      setProvisioning(data.provisioning);
      if (!newOrgDevicePrefix.trim()) {
        setNewOrgDevicePrefix(data.device_id_prefix);
      }
      if (!newOrgBqTable.trim()) {
        setNewOrgBqTable(data.bq_requests_table);
      }
      if (data.looker_linking_url) {
        setCreateOrgResult(data);
      }
    } catch (e: any) {
      setError(String(e.message || e));
    }
  };

  const registerLookerReport = async (slug: string, reportIdOrUrl: string) => {
    if (!slug || !reportIdOrUrl.trim()) return;
    setError(null);
    try {
      const res = await adminFetch('/v1/admin/organizations/looker_report', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_slug: slug,
          looker_report_id: reportIdOrUrl.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.detail || res.statusText);
      }
      setMessage('Looker report registered.');
      setRegisterLookerId('');
    } catch (e: any) {
      setError(String(e.message || e));
    }
  };

  const createOrganization = async () => {
    const slug = newOrgSlug.trim().toLowerCase();
    if (!slug) return;
    setError(null);
    setMessage(null);
    const payload: Record<string, unknown> = {
      slug,
      timezone: newOrgTimezone,
    };
    if (newOrgDevicePrefix.trim()) {
      payload.device_id_prefix = newOrgDevicePrefix.trim();
    }
    if (newOrgBqTable.trim()) {
      payload.bq_requests_table = newOrgBqTable.trim();
    }
    if (newOrgInitialEmail.trim()) {
      payload.initial_email = newOrgInitialEmail.trim();
    }
    payload.provision_bq = provisionBq;
    payload.provision_looker = provisionLooker;
    if (newOrgLookerReportId.trim()) {
      payload.looker_report_id = newOrgLookerReportId.trim();
    }
    try {
      const res = await adminFetch('/v1/admin/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.detail || res.statusText);
      }
      const data: CreateOrgResult = await res.json();
      setCreateOrgResult(data);
      setProvisioning(data.provisioning);
      setOrgSlug(data.org_slug);
      setMessage(`Organization "${data.org_slug}" registered in the config database.`);
      reloadOrgsList();
    } catch (e: any) {
      setError(String(e.message || e));
    }
  };

  const removeBreak = async (breakId: number) => {
    setError(null);
    try {
      const res = await adminFetch(
        `/v1/admin/prediction_breaks/${breakId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Remove failed (${res.status})`);
      }
      setBreaks((prev) => prev.filter((b) => b.id !== breakId));
      setMessage('Prediction break removed.');
      await loadConfig(orgSlug);
    } catch (e: any) {
      setError(String(e.message || e));
    }
  };

  const schedulePanel = schedule && (
    <Box sx={{ pt: 1 }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Timezone</InputLabel>
        <Select
          value={schedule.timezone}
          label="Timezone"
          onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })}
        >
          {timezones.map((tz) => (
            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TimePicker
            label="Work start"
            slotProps={timePickerSlotProps}
            value={parseTime(schedule.work_hours?.start_time)}
            onChange={(v) => setSchedule({
              ...schedule,
              work_hours: {
                ...schedule.work_hours,
                start_time: formatTime(v),
                end_time: schedule.work_hours?.end_time,
              },
            })}
          />
          <TimePicker
            label="Work end"
            slotProps={timePickerSlotProps}
            value={parseTime(schedule.work_hours?.end_time)}
            onChange={(v) => setSchedule({
              ...schedule,
              work_hours: {
                start_time: schedule.work_hours?.start_time,
                end_time: formatTime(v),
              },
            })}
          />
        </Box>
      </LocalizationProvider>
      <Box display="flex" gap={2} mb={2}>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Work week start</InputLabel>
          <Select
            value={schedule.work_days?.start_day ?? 0}
            label="Work week start"
            onChange={(e) => setSchedule({
              ...schedule,
              work_days: {
                start_day: Number(e.target.value),
                end_day: schedule.work_days?.end_day ?? 4,
              },
            })}
          >
            {DAY_LABELS.map((label, i) => (
              <MenuItem key={label} value={i}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Work week end</InputLabel>
          <Select
            value={schedule.work_days?.end_day ?? 4}
            label="Work week end"
            onChange={(e) => setSchedule({
              ...schedule,
              work_days: {
                start_day: schedule.work_days?.start_day ?? 0,
                end_day: Number(e.target.value),
              },
            })}
          >
            {DAY_LABELS.map((label, i) => (
              <MenuItem key={label} value={i}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FormControlLabel
        control={(
          <Checkbox
            checked={schedule.speech_lifetime}
            onChange={(e) => setSchedule({
              ...schedule, speech_lifetime: e.target.checked,
            })}
          />
        )}
        label="Speech lifetime"
      />
      <FormControlLabel
        control={(
          <Checkbox
            checked={schedule.save_human_speech}
            onChange={(e) => setSchedule({
              ...schedule, save_human_speech: e.target.checked,
            })}
          />
        )}
        label="Save human speech audio"
      />
      <Box mt={2}>
        <Button variant="contained" onClick={saveSchedule}>Save schedule</Button>
      </Box>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        During work hours and active breaks, prediction is off (should_predict=false).
      </Typography>
    </Box>
  );

  const breaksPanel = (
    <Box sx={{ pt: 1 }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Break type</InputLabel>
        <Select
          value={breakType}
          label="Break type"
          onChange={(e) => setBreakType(e.target.value)}
        >
          <MenuItem value="duration">From now for hours/minutes</MenuItem>
          <MenuItem value="until_clock">From now until clock time</MenuItem>
          <MenuItem value="clock_range">Clock range today</MenuItem>
        </Select>
      </FormControl>
      {breakType === 'duration' && (
        <Box display="flex" gap={2} mb={2}>
          <TextField
            type="number"
            label="Hours"
            value={durationHours}
            onChange={(e) => setDurationHours(Number(e.target.value))}
          />
          <TextField
            type="number"
            label="Minutes"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
          />
        </Box>
      )}
      {breakType === 'until_clock' && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Until (org timezone)"
            slotProps={timePickerSlotProps}
            value={untilClock}
            onChange={setUntilClock}
          />
        </LocalizationProvider>
      )}
      {breakType === 'clock_range' && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box display="flex" gap={2}>
            <TimePicker
              label="From"
              slotProps={timePickerSlotProps}
              value={rangeStart}
              onChange={setRangeStart}
            />
            <TimePicker
              label="To"
              slotProps={timePickerSlotProps}
              value={rangeEnd}
              onChange={setRangeEnd}
            />
          </Box>
        </LocalizationProvider>
      )}
      <TextField
        label="Label (optional)"
        value={breakLabel}
        onChange={(e) => setBreakLabel(e.target.value)}
        fullWidth
        sx={{ mt: 2, mb: 2 }}
      />
      <Button variant="contained" onClick={addBreak}>Add break</Button>
      <Box mt={3}>
        {breaks.map((b) => (
          <Box key={b.id} display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              [{b.break_type}] {new Date(b.starts_at).toLocaleString()} –{' '}
              {new Date(b.ends_at).toLocaleString()}
              {b.label ? ` (${b.label})` : ''}
            </Typography>
            <Button
              size="small"
              onClick={() => removeBreak(Number(b.id))}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const usersPanel = (
    <Box sx={{ pt: 1 }}>
      {users.map((row) => (
        <Box key={row.email} display="flex" gap={1} mb={1}>
          <Typography sx={{ flex: 1 }}>{row.email}</Typography>
          <Button size="small" onClick={() => removeUser(row.email)}>
            Remove
          </Button>
        </Box>
      ))}
      <Box display="flex" gap={1} mt={2}>
        <TextField
          label="User email"
          value={newUserEmail}
          onChange={(e) => setNewUserEmail(e.target.value)}
          fullWidth
        />
        <Button variant="contained" onClick={addUser}>Add</Button>
      </Box>
    </Box>
  );

  const newCustomerPanel = isMaster && (
    <Box sx={{ pt: 1 }}>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Registers the customer in the config database and can provision the BigQuery
        view and Looker Studio copy link (API needs GOOGLE_APPLICATION_CREDENTIALS
        for BQ). After creation, run{' '}
        <code>make sync-customer-k8s ARGS=&quot;--slug=&lt;slug&gt;&quot;</code>{' '}
        locally to generate k8s/repo files. Looker still needs one browser step
        unless you paste an existing report URL below.
      </Typography>
      <TextField
        label="Organization slug"
        helperText="Lowercase, e.g. ostgota or my-customer"
        value={newOrgSlug}
        onChange={(e) => setNewOrgSlug(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Timezone</InputLabel>
        <Select
          value={newOrgTimezone}
          label="Timezone"
          onChange={(e) => setNewOrgTimezone(e.target.value)}
        >
          {timezones.map((tz) => (
            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Device ID prefix"
        helperText="Defaults to slug- (e.g. ostgota-)"
        value={newOrgDevicePrefix}
        onChange={(e) => setNewOrgDevicePrefix(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="BigQuery requests table / view"
        helperText="Defaults to predict_requests_<slug>"
        value={newOrgBqTable}
        onChange={(e) => setNewOrgBqTable(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="First user email (optional)"
        value={newOrgInitialEmail}
        onChange={(e) => setNewOrgInitialEmail(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Looker report URL or ID (optional)"
        helperText="If you already have a report, paste it here. Otherwise use the create link after provisioning."
        value={newOrgLookerReportId}
        onChange={(e) => setNewOrgLookerReportId(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={(
          <Checkbox
            checked={provisionBq}
            onChange={(e) => setProvisionBq(e.target.checked)}
          />
        )}
        label="Create BigQuery view (ser.predict_requests_<slug>)"
        sx={{ display: 'block' }}
      />
      <FormControlLabel
        control={(
          <Checkbox
            checked={provisionLooker}
            onChange={(e) => setProvisionLooker(e.target.checked)}
          />
        )}
        label="Generate Looker Studio copy link (famb template)"
        sx={{ display: 'block', mb: 1 }}
      />
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Button variant="outlined" onClick={previewProvisioning}>
          Preview checklist
        </Button>
        <Button variant="contained" onClick={createOrganization}>
          Create organization
        </Button>
      </Box>
      {createOrgResult && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Registered: {createOrgResult.org_slug} — devices use prefix{' '}
          <strong>{createOrgResult.device_id_prefix}</strong>, BQ view{' '}
          <strong>{createOrgResult.bq_requests_table}</strong>
          {createOrgResult.bq_view_created && (
            <>
              <br />
              BigQuery view created.
              {createOrgResult.bq_message ? ` ${createOrgResult.bq_message}` : ''}
            </>
          )}
          {createOrgResult.looker_linking_url && (
            <>
              <br />
              <a href={createOrgResult.looker_linking_url} target="_blank" rel="noreferrer">
                Open Looker Studio — create report for this customer
              </a>
            </>
          )}
          {createOrgResult.looker_embed_url && (
            <>
              <br />
              Looker embed configured: {createOrgResult.looker_report_id}
            </>
          )}
          {createOrgResult.customer_runbook_path && (
            <>
              <br />
              Run k8s/repo sync locally:{' '}
              <code>make sync-customer-k8s ARGS=&quot;--slug={createOrgResult.org_slug}&quot;</code>
              {' '}(creates {createOrgResult.customer_runbook_path} and related files)
            </>
          )}
        </Alert>
      )}
      {isMaster && orgSlug && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Register Looker report for {orgSlug}
          </Typography>
          <Box display="flex" gap={1}>
            <TextField
              label="Report URL or UUID"
              value={registerLookerId}
              onChange={(e) => setRegisterLookerId(e.target.value)}
              fullWidth
              size="small"
            />
            <Button
              variant="outlined"
              onClick={() => registerLookerReport(orgSlug, registerLookerId)}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
      {provisioning && provisioning.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Remaining setup (manual)
          </Typography>
          {provisioning.map((step) => (
            <Box key={step.id} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.detail}
              </Typography>
              {step.link && (
                <Typography variant="body2">
                  <a href={step.link} target="_blank" rel="noreferrer">
                    Open link
                  </a>
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return renderBoilerPlate(
    <div style={pageLayout}>
      <div>
        <Paper sx={formFieldSx}>
          <Typography variant="subtitle1" sx={{ mb: '2rem' }}>Organization settings</Typography>
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isMaster ? (
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                value={orgSlug}
                label="Organization"
                onChange={(e) => setOrgSlug(e.target.value)}
              >
                {orgs.map((o) => (
                  <MenuItem key={o.name} value={o.name}>{o.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2">
              Organization: {orgSlug}
            </Typography>
          )}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2, mb: '1rem' }}>
            <Tab label="Schedule" />
            <Tab label="Prediction breaks" />
            <Tab label="Events" />
            <Tab label="Users" />
            {isMaster && <Tab label="New customer" />}
          </Tabs>
          {tab === 0 && schedulePanel}
          {tab === 1 && breaksPanel}
          {tab === 2 && (
            <Box sx={{ pt: 1 }}>
              {events.map((event, index) => (
                <Box key={`${event}-${index}`} display="flex" gap={1} mb={1}>
                  <Typography sx={{ flex: 1 }}>{event}</Typography>
                  {event !== CASUAL_EVENT && (
                    <Button size="small" onClick={() => removeEvent(index)}>
                      Remove
                    </Button>
                  )}
                </Box>
              ))}
              <Box display="flex" gap={1} mt={2}>
                <TextField
                  label="New event"
                  value={newEvent}
                  onChange={(e) => setNewEvent(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addEvent()}
                  fullWidth
                />
                <Button variant="contained" onClick={addEvent}>Add</Button>
              </Box>
              <Button variant="contained" sx={{ mt: 2 }} onClick={saveEvents}>
                Save events
              </Button>
            </Box>
          )}
          {tab === 3 && usersPanel}
          {isMaster && tab === 4 && newCustomerPanel}
        </Paper>
      </div>
    </div>,
  );
};

export default withRouter(OrgAdmin);
